import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DAILY_KEY, TIER_KEY } from '@/features/agent-metrics/useAgentMetrics';
import { PROFILE_SUMMARY_KEY, HISTORY_KEY } from '@/features/agent-progress/progressKeys';
import { screeningApi } from './screening.api';
import type {
  HrsnDomain,
  LogTimeEventInput,
  SaveAnswerInput,
  SubmitScreeningInput,
  TimeEventType,
} from '@/types/api';

/**
 * Imperative time-event logger. Fire-and-forget; errors swallowed.
 *
 * Caller does e.g. logEvent('call_started').
 */
export function useTimeEventLogger(screeningId: string) {
  const lastFiredRef = useRef<Map<string, number>>(new Map());

  return useCallback(
    async (eventType: TimeEventType, eventData?: Record<string, unknown>): Promise<void> => {
      // Coalesce duplicate events fired within 500ms (e.g., focus events fire often).
      const key = `${eventType}:${JSON.stringify(eventData ?? {})}`;
      const last = lastFiredRef.current.get(key) ?? 0;
      if (Date.now() - last < 500) return;
      lastFiredRef.current.set(key, Date.now());

      try {
        await screeningApi.logEvent(screeningId, { eventType, eventData });
      } catch {
        // best-effort
      }
    },
    [screeningId],
  );
}

/**
 * Debounced auto-save per domain.
 *
 * Each domain has its own debounce timer. Calling save(domain, payload)
 * resets that domain's timer; after `delayMs` of no further calls, the
 * latest payload is sent.
 */
export function useDebouncedSaveAnswer(screeningId: string, delayMs = 800) {
  const timersRef = useRef<Map<HrsnDomain, ReturnType<typeof setTimeout>>>(new Map());
  const pendingRef = useRef<Map<HrsnDomain, SaveAnswerInput>>(new Map());

  const mutation = useMutation({
    mutationFn: (input: SaveAnswerInput) => screeningApi.saveAnswer(screeningId, input),
  });

  const schedule = useCallback(
    (input: SaveAnswerInput) => {
      pendingRef.current.set(input.domain, input);
      const existing = timersRef.current.get(input.domain);
      if (existing) clearTimeout(existing);
      const t = setTimeout(() => {
        const payload = pendingRef.current.get(input.domain);
        if (!payload) return;
        pendingRef.current.delete(input.domain);
        timersRef.current.delete(input.domain);
        mutation.mutate(payload);
      }, delayMs);
      timersRef.current.set(input.domain, t);
    },
    [delayMs, mutation],
  );

  /** Force-flush any pending domains immediately (e.g., on unmount or submit). */
  const flush = useCallback(async (): Promise<void> => {
    const promises: Array<Promise<unknown>> = [];
    pendingRef.current.forEach((payload, domain) => {
      const t = timersRef.current.get(domain);
      if (t) clearTimeout(t);
      promises.push(screeningApi.saveAnswer(screeningId, payload));
    });
    pendingRef.current.clear();
    timersRef.current.clear();
    await Promise.allSettled(promises);
  }, [screeningId]);

  // Cleanup timers on unmount
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
    };
  }, []);

  return {
    schedule,
    flush,
    isSaving: mutation.isPending,
    lastSaveError: mutation.error as Error | null,
  };
}

export function useSubmitScreening(screeningId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // Cluster H (2026-05-17): shared mutationKey across parent + sub
    // submits enables an `isMutating({mutationKey:['screenings']}) === 1`
    // guard at the call site, preventing parent + sub from racing each
    // other into the backend. LD-23. Pattern from tkdodo TanStack docs.
    mutationKey: ['screenings'],
    mutationFn: (input: SubmitScreeningInput) => screeningApi.submit(screeningId, input),
    onSuccess: (resp) => {
      // Backend auto-completes any matching booking for this patient+agent on
      // submit (see screenings.service.ts). The submit response now includes
      // autoCompletedBookingIds so we can confirm on the client side. We
      // invalidate AND force-refetch the active bookings caches so the queue
      // card decrements instantly (without waiting on the 60s polling
      // interval). Fix 2026-05-09: previously the card kept showing the stale
      // count until the next poll fired.
      queryClient.invalidateQueries({
        queryKey: ['bookings.me'],
        exact: false,
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: ['bookings.due-soon'],
        refetchType: 'active',
      });
      // Submitting a screening flips the agent's "today" counters, tier
      // progress, dashboard cards, flair panel, and recent-completed list.
      // Invalidate every queryKey that surfaces a 'today' or queue metric so
      // the UI updates instantly instead of waiting on the 60s poll.
      // Fix 2026-05-09 (tz-fix): paired with backend NY-tz fix in
      // agent-metrics.service.ts.
      queryClient.invalidateQueries({ queryKey: DAILY_KEY, refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: TIER_KEY, refetchType: 'active' });
      // Cluster M v0.5.0 — invalidate the consolidated profile-summary + history
      queryClient.invalidateQueries({ queryKey: PROFILE_SUMMARY_KEY, refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: HISTORY_KEY(12), refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['agents', 'me', 'dashboard'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['agents', 'me', 'flair'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['agents', 'me', 'completed'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['agents', 'me', 'call-attempts'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['agents', 'me', 'upload-status'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['queue'], refetchType: 'active' });
      // Defensive: also explicitly refetch in case any listening component
      // is mounted but its query is paused. (Cheap, idempotent.)
      void queryClient.refetchQueries({ queryKey: ['bookings.me'], exact: false });
      void queryClient.refetchQueries({ queryKey: ['bookings.due-soon'] });
      // Light log for staging verification (stripped at minify in prod build).
      const ids = (resp as { autoCompletedBookingIds?: string[] } | undefined)?.autoCompletedBookingIds;
      if (ids && ids.length > 0) {
        // eslint-disable-next-line no-console
        console.debug('[screening submit] auto-completed booking ids:', ids);
      }
    },
  });
}

/** Re-export for convenience. */
export type { LogTimeEventInput, SubmitScreeningInput };
